import { useMemo } from 'react';
import type { ApplicationFlatTableEntity } from 'src/components/context/AppContextModels';
import { useAppContext } from 'src/components/context/AppContextProvider';
import { RecentEntityType } from '../RecentsConstants';
import type { RecentsHydrator } from './types';

/**
 * App hydrator.
 *
 * Lives as a hook (not a pure function) because its source of truth is
 * `AppContext.parsedApplications`, which is a React context. Consumers call
 * `useAppHydrator()` and pass the result into `useRecents(APP, hydrator)`.
 *
 * Returns `null` for:
 * - Apps not in the catalog (deleted).
 * - Apps whose `isActive === false` (deactivated — treated like deletes).
 *
 * The returned hydrator object is memoized against `parsedApplications` so
 * referential equality holds across renders that don't change the catalog.
 * This matters because `useRecents` treats a new hydrator identity as a
 * signal to re-hydrate.
 */
export function useAppHydrator(): RecentsHydrator<ApplicationFlatTableEntity> {
  const { parsedApplications } = useAppContext();

  // Build an id -> entity map once per catalog change. O(1) lookups at
  // render time instead of O(n) .find() per entry.
  const activeAppsById = useMemo(() => {
    const map = new Map<string, ApplicationFlatTableEntity>();
    for (const app of parsedApplications) {
      if (app.isActive) {
        map.set(app.id, app);
      }
    }
    return map;
  }, [parsedApplications]);

  return useMemo<RecentsHydrator<ApplicationFlatTableEntity>>(
    () => ({
      type: RecentEntityType.APP,
      resolve(entityId) {
        return activeAppsById.get(entityId) ?? null;
      }
    }),
    [activeAppsById]
  );
}
